import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RotateCw, AlertTriangle, Clock, Compass, BarChart3 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { compareDecimalStrings } from '../../shared/domain/decimal';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';

export interface SectorBreadth {
  total: number;
  advancing: number;
  declining: number;
  flat: number;
  breadth_ratio: string;
}

export interface SectorLeaderStock {
  symbol: string;
  name: string;
  pct_chg: string;
}

export interface SectorRotationItem {
  sector_code: string;
  sector_name: string;
  pct_chg: string;
  turnover_total_million: string;
  net_inflow_million: string;
  rs_diff: string;
  rs_ratio: string;
  rs_momentum: string;
  rrg_quadrant: 'LEADING' | 'WEAKENING' | 'LAGGING' | 'IMPROVING' | string;
  breadth: SectorBreadth;
  leader_stock: SectorLeaderStock;
}

export interface SectorRotationResponse {
  status: 'fresh' | 'stale' | 'empty' | 'disconnected' | 'loading' | string;
  trade_date: string;
  session_kind: string;
  total_sectors: number;
  items: SectorRotationItem[];
  meta?: {
    state: string;
    scan_completed_at?: string;
    benchmark_code?: string;
    benchmark_pct_chg?: string;
    stale_reason?: string | null;
  };
}

export interface AShareSectorRotationViewProps {
  colorMode?: 'mainland' | 'international';
}

type SortField = 'pct_chg' | 'momentum' | 'net_inflow' | 'rs_diff' | 'breadth_ratio';
type QuadrantFilter = 'ALL' | 'LEADING' | 'IMPROVING' | 'WEAKENING' | 'LAGGING';

export const AShareSectorRotationView: React.FC<AShareSectorRotationViewProps> = ({
  colorMode = 'mainland',
}) => {
  const [data, setData] = useState<SectorRotationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [tradeDate, setTradeDate] = useState<string>('');
  const [sessionKind, setSessionKind] = useState<string>('DAY_CLOSE');
  const [sortBy, setSortBy] = useState<SortField>('pct_chg');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [selectedQuadrant, setSelectedQuadrant] = useState<QuadrantFilter>('ALL');
  const [hoveredSector, setHoveredSector] = useState<string | null>(null);

  const fetchSectorRotation = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tradeDate) params.append('trade_date', tradeDate);
      params.append('session_kind', sessionKind);
      params.append('sort_by', sortBy);
      params.append('order', sortOrder);
      params.append('limit', '50');

      const res = await apiClient.get<SectorRotationResponse>(
        `/api/v1/market/a-share/sector/rotation?${params.toString()}`
      );
      setData(res);
      if (res?.trade_date && !tradeDate) {
        setTradeDate(res.trade_date);
      }
    } catch (err) {
      setData({
        status: 'disconnected',
        trade_date: tradeDate,
        session_kind: sessionKind,
        total_sectors: 0,
        items: [],
        meta: {
          state: 'disconnected',
          stale_reason: '无法连接至 Market-Hub MultiAsset 数据服务',
        },
      });
    } finally {
      setLoading(false);
    }
  }, [tradeDate, sessionKind, sortBy, sortOrder]);

  useEffect(() => {
    fetchSectorRotation();
  }, [fetchSectorRotation]);

  const getColor = useCallback(
    (pctStr: string | undefined): string => {
      if (!pctStr) return 'var(--text-secondary)';
      const isPositive = pctStr.startsWith('+') || (!pctStr.startsWith('-') && pctStr !== '0.0000' && pctStr !== '0.00');
      const isNegative = pctStr.startsWith('-');
      if (!isPositive && !isNegative) return 'var(--text-muted)';
      if (colorMode === 'mainland') {
        return isPositive ? 'var(--color-bearish)' : 'var(--color-bullish)'; // Red up, Green down
      }
      return isPositive ? 'var(--color-bullish)' : 'var(--color-bearish)'; // Green up, Red down
    },
    [colorMode]
  );

  const upColor = colorMode === 'mainland' ? 'var(--color-bearish)' : 'var(--color-bullish)';
  const downColor = colorMode === 'mainland' ? 'var(--color-bullish)' : 'var(--color-bearish)';

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    if (selectedQuadrant === 'ALL') return data.items;
    return data.items.filter((it) => it.rrg_quadrant === selectedQuadrant);
  }, [data?.items, selectedQuadrant]);

  const quadrantStats = useMemo(() => {
    const counts = { LEADING: 0, IMPROVING: 0, WEAKENING: 0, LAGGING: 0 };
    if (!data?.items) return counts;
    data.items.forEach((it) => {
      const q = it.rrg_quadrant as keyof typeof counts;
      if (counts[q] !== undefined) counts[q]++;
    });
    return counts;
  }, [data?.items]);

  const stateBadgeInfo = useMemo(() => {
    const state = data?.status || (loading ? 'loading' : 'disconnected');
    switch (state) {
      case 'fresh':
        return { label: '● 实时数据 (FRESH)', bg: 'var(--badge-bullish-bg)', color: 'var(--color-bullish)', border: 'var(--badge-bullish-border)' };
      case 'stale':
        return { label: '▲ 历史盘后 (STALE)', bg: 'var(--badge-warning-bg)', color: 'var(--color-warning)', border: 'var(--badge-warning-border)' };
      case 'empty':
        return { label: '○ 空集 (EMPTY)', bg: 'var(--tag-inactive-bg)', color: 'var(--text-muted)', border: 'var(--border-subtle)' };
      case 'disconnected':
        return { label: '✕ 离线 (DISCONNECTED)', bg: 'var(--alert-bearish-bg)', color: 'var(--color-bearish)', border: 'var(--alert-bearish-border)' };
      case 'loading':
      default:
        return { label: '◌ 同步计算中 (LOADING)', bg: 'var(--badge-info-bg)', color: 'var(--accent-cyan)', border: 'var(--badge-info-border)' };
    }
  }, [data?.status, loading]);

  const getQuadrantStyle = (quadrant: string) => {
    switch (quadrant) {
      case 'LEADING':
        return {
          bg: 'var(--badge-bullish-bg)',
          color: 'var(--color-bullish)',
          border: 'var(--badge-bullish-border)',
        };
      case 'IMPROVING':
        return {
          bg: 'var(--badge-info-bg)',
          color: 'var(--accent-cyan)',
          border: 'var(--badge-info-border)',
        };
      case 'WEAKENING':
        return {
          bg: 'var(--badge-warning-bg)',
          color: 'var(--color-warning)',
          border: 'var(--badge-warning-border)',
        };
      case 'LAGGING':
      default:
        return {
          bg: 'var(--badge-bearish-bg)',
          color: 'var(--color-bearish)',
          border: 'var(--badge-bearish-border)',
        };
    }
  };

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <RotateCw size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.sectorRotation.zh}</h2>
            <span
              style={{
                padding: '3px 10px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
                background: stateBadgeInfo.bg,
                color: stateBadgeInfo.color,
                border: `1px solid ${stateBadgeInfo.border}`,
              }}
            >
              {stateBadgeInfo.label}
            </span>
          </div>
          <span className="bilingual-header-subtitle-en" title="SECTOR ROTATION & MOMENTUM RRG COMPASS">{ASHARE_BILINGUAL.sectorRotation.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            申万一级/二级行业成交额加权涨跌幅、沪深300相对强弱比率与四象限动量轮动追踪
          </p>
        </div>

        {/* Benchmark & Session Info Badge */}
        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {data?.meta?.benchmark_code && (
            <div
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-card)',
                fontSize: '12px',
                fontFamily: 'var(--font-mono)',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <span style={{ color: 'var(--text-secondary)' }}>基准沪深300:</span>
              <span style={{ fontWeight: 700, color: getColor(data.meta.benchmark_pct_chg) }}>
                {data.meta.benchmark_pct_chg ? `${data.meta.benchmark_pct_chg}%` : '--'}
              </span>
            </div>
          )}

          <div
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              background: 'var(--bg-card)',
              border: '1px solid var(--border-card)',
              fontSize: '12px',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-muted)',
            }}
          >
            <span>时延窗口 W ≥ 3s</span>
            <span style={{ margin: '0 6px' }}>|</span>
            <span>100% Pure Decimal</span>
          </div>
        </div>
      </div>

      {/* Disconnected Alert Banner */}
      {data?.status === 'disconnected' && (
        <div
          style={{
            padding: '14px 18px',
            marginBottom: '20px',
            borderRadius: '8px',
            background: 'var(--alert-bearish-bg)',
            border: '1px solid var(--alert-bearish-border)',
            color: 'var(--color-bearish)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '13px',
          }}
        >
          <AlertTriangle size={18} />
          <div>
            <strong>服务连接不可用 (DISCONNECTED)</strong>: {data.meta?.stale_reason || '数据库或只读网关断开连接，展示离线状态，杜绝假数据兜底。'}
          </div>
        </div>
      )}

      {/* Stale Warning Banner */}
      {data?.status === 'stale' && (
        <div
          style={{
            padding: '12px 18px',
            marginBottom: '20px',
            borderRadius: '8px',
            background: 'var(--badge-warning-bg)',
            border: '1px solid var(--badge-warning-border)',
            color: 'var(--color-warning)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '13px',
          }}
        >
          <Clock size={16} />
          <div>
            <strong>数据滞后提示 (STALE)</strong>: {data.meta?.stale_reason || '当前展示为上一闭市交易日已归档数据，盘中未更新。'}
          </div>
        </div>
      )}

      {/* Control Bar: Filters, Sessions & Sort Fields */}
      <div
        style={{
          padding: '16px 20px',
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '14px',
        }}
      >
        {/* Quadrant Quick Filter Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginRight: '4px' }}>
            动量象限:
          </span>
          <button
            type="button"
            onClick={() => setSelectedQuadrant('ALL')}
            style={{
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: selectedQuadrant === 'ALL' ? 700 : 500,
              background: selectedQuadrant === 'ALL' ? 'var(--text-primary)' : 'var(--bg-base)',
              color: selectedQuadrant === 'ALL' ? 'var(--text-inverse)' : 'var(--text-secondary)',
              border: '1px solid var(--border-card)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            全部 ({data?.total_sectors || 0})
          </button>
          <button
            type="button"
            onClick={() => setSelectedQuadrant('LEADING')}
            style={{
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: selectedQuadrant === 'LEADING' ? 700 : 500,
              background: selectedQuadrant === 'LEADING' ? 'var(--color-bullish)' : 'var(--badge-bullish-bg)',
              color: selectedQuadrant === 'LEADING' ? 'var(--text-inverse)' : 'var(--color-bullish)',
              border: '1px solid var(--badge-bullish-border)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            领跑 LEADING ({quadrantStats.LEADING})
          </button>
          <button
            type="button"
            onClick={() => setSelectedQuadrant('IMPROVING')}
            style={{
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: selectedQuadrant === 'IMPROVING' ? 700 : 500,
              background: selectedQuadrant === 'IMPROVING' ? 'var(--accent-cyan)' : 'var(--badge-info-bg)',
              color: selectedQuadrant === 'IMPROVING' ? 'var(--text-inverse)' : 'var(--accent-cyan)',
              border: '1px solid var(--badge-info-border)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            改善 IMPROVING ({quadrantStats.IMPROVING})
          </button>
          <button
            type="button"
            onClick={() => setSelectedQuadrant('WEAKENING')}
            style={{
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: selectedQuadrant === 'WEAKENING' ? 700 : 500,
              background: selectedQuadrant === 'WEAKENING' ? 'var(--color-warning)' : 'var(--badge-warning-bg)',
              color: selectedQuadrant === 'WEAKENING' ? 'var(--text-inverse)' : 'var(--color-warning)',
              border: '1px solid var(--badge-warning-border)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            走弱 WEAKENING ({quadrantStats.WEAKENING})
          </button>
          <button
            type="button"
            onClick={() => setSelectedQuadrant('LAGGING')}
            style={{
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: selectedQuadrant === 'LAGGING' ? 700 : 500,
              background: selectedQuadrant === 'LAGGING' ? 'var(--color-bearish)' : 'var(--alert-bearish-bg)',
              color: selectedQuadrant === 'LAGGING' ? 'var(--text-inverse)' : 'var(--color-bearish)',
              border: '1px solid var(--alert-bearish-border)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            落后 LAGGING ({quadrantStats.LAGGING})
          </button>
        </div>

        {/* Sort & Session Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>排序指标:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortField)}
              style={{
                padding: '5px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                background: 'var(--bg-base)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-card)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <option value="pct_chg">加权涨跌幅 (pct_chg)</option>
              <option value="momentum">相对动量 (momentum)</option>
              <option value="net_inflow">成交总额 (turnover)</option>
              <option value="rs_diff">超额收益 (rs_diff)</option>
              <option value="breadth_ratio">多头广度 (breadth)</option>
            </select>
          </div>

          <button
            type="button"
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            style={{
              padding: '5px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              background: 'var(--bg-base)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-card)',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {sortOrder === 'desc' ? '降序 ↓' : '升序 ↑'}
          </button>

          <button
            type="button"
            onClick={() => setSessionKind(sessionKind === 'DAY_CLOSE' ? 'MIDDAY' : 'DAY_CLOSE')}
            style={{
              padding: '5px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              background: 'var(--bg-base)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-card)',
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {sessionKind === 'DAY_CLOSE' ? '闭市盘 (DAY_CLOSE)' : '午盘 (MIDDAY)'}
          </button>

          <button
            type="button"
            onClick={fetchSectorRotation}
            disabled={loading}
            style={{
              padding: '5px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              background: 'var(--text-primary)',
              color: 'var(--text-inverse)',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? '刷新中...' : '刷新'}
          </button>
        </div>
      </div>

      {/* Interactive 4-Quadrant RRG Compass Visualizer */}
      <div
        style={{
          padding: '24px',
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
          marginBottom: '20px',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Compass size={16} className="icon-cyan" /> 相对动量轮动图谱 (Relative Rotation Graph · RRG)
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
              横轴: 相对强弱比率 RS-Ratio (基准线 100) · 纵轴: 动量加速度 RS-Momentum (基准线 100)
            </p>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            顺时针轮动: 改善 → 领跑 → 走弱 → 落后 → 改善
          </div>
        </div>

        {/* 2D Quadrant Matrix Visual Container */}
        <div
          style={{
            position: 'relative',
            height: '380px',
            borderRadius: '6px',
            border: '1px solid var(--border-card)',
            background: 'var(--bg-base)',
            overflow: 'hidden',
          }}
        >
          {/* Quadrant Background Panels */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', height: '100%', width: '100%' }}>
            {/* Top-Left: IMPROVING */}
            <div style={{ background: 'var(--badge-info-bg)', borderRight: '1px dashed var(--border-subtle)', borderBottom: '1px dashed var(--border-subtle)', padding: '10px' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent-cyan)', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', padding: '2px 6px', borderRadius: '3px' }}>
                改善象限 (IMPROVING)
              </span>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '6px' }}>左侧反弹 · 动量触底回升</span>
            </div>

            {/* Top-Right: LEADING */}
            <div style={{ background: 'var(--badge-bullish-bg)', borderBottom: '1px dashed var(--border-subtle)', padding: '10px', textAlign: 'right' }}>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginRight: '6px' }}>主升领涨 · 超额收益加速</span>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-bullish)', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', padding: '2px 6px', borderRadius: '3px' }}>
                领跑象限 (LEADING)
              </span>
            </div>

            {/* Bottom-Left: LAGGING */}
            <div style={{ background: 'var(--alert-bearish-bg)', borderRight: '1px dashed var(--border-subtle)', padding: '10px', display: 'flex', alignItems: 'flex-end' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-bearish)', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', padding: '2px 6px', borderRadius: '3px' }}>
                落后象限 (LAGGING)
              </span>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '6px' }}>弱势磨底 · 相对收益承压</span>
            </div>

            {/* Bottom-Right: WEAKENING */}
            <div style={{ background: 'var(--badge-warning-bg)', padding: '10px', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginRight: '6px' }}>见顶走弱 · 警惕滞涨分化</span>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-warning)', background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', padding: '2px 6px', borderRadius: '3px' }}>
                走弱象限 (WEAKENING)
              </span>
            </div>
          </div>

          {/* Plotted Sector Badges */}
          {filteredItems.map((sec) => {
            const ratioVal = Number(sec.rs_ratio) || 100;
            const momVal = Number(sec.rs_momentum) || 100;
            const leftPct = Math.max(5, Math.min(95, ((ratioVal - 85) / 30) * 100));
            const topPct = Math.max(8, Math.min(92, 100 - ((momVal - 85) / 30) * 100));

            const isHovered = hoveredSector === sec.sector_code;

            return (
              <div
                key={sec.sector_code}
                onMouseEnter={() => setHoveredSector(sec.sector_code)}
                onMouseLeave={() => setHoveredSector(null)}
                style={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  top: `${topPct}%`,
                  transform: 'translate(-50%, -50%)',
                  padding: isHovered ? '4px 8px' : '2px 6px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: isHovered ? 700 : 500,
                  fontFamily: 'var(--font-mono)',
                  background: isHovered ? 'var(--text-primary)' : 'var(--bg-card)',
                  color: isHovered ? 'var(--text-inverse)' : 'var(--text-primary)',
                  border: isHovered ? '2px solid var(--border-focus)' : '1px solid var(--border-card)',
                  boxShadow: 'var(--shadow-card)',
                  cursor: 'pointer',
                  zIndex: isHovered ? 20 : 10,
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
                title={`${sec.sector_name} (${sec.sector_code})\n涨跌幅: ${sec.pct_chg}%\nRS-Ratio: ${sec.rs_ratio}\nRS-Momentum: ${sec.rs_momentum}\n领涨龙头: ${sec.leader_stock.name} (${sec.leader_stock.pct_chg}%)`}
              >
                {sec.sector_name}
                {isHovered && (
                  <span style={{ marginLeft: '4px', color: getColor(sec.pct_chg) }}>
                    {sec.pct_chg}%
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Sector Ranking & Heatmap Table */}
      <div
        style={{
          borderRadius: '8px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-card)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-card)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <BarChart3 size={16} className="icon-cyan" /> 板块轮动数据总表 (共 {filteredItems.length} 个行业板块)
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            金额单位: 百万人民币 (M) · 占比: 申万全市场成分股成交额加权
          </div>
        </div>

        {/* Table Content */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: 'var(--bg-base)', borderBottom: '1px solid var(--border-card)' }}>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>行业板块</th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>象限归属</th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' }}>
                  加权涨跌幅
                </th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' }}>
                  总成交额 (M)
                </th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' }}>
                  超额收益 (RS_diff)
                </th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' }}>
                  RS_Ratio
                </th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' }}>
                  RS_Momentum
                </th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>多头广度 (涨/跌/平)</th>
                <th style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--text-secondary)' }}>领涨龙头股</th>
              </tr>
            </thead>
            <tbody>
              {loading && filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    正在计算全行业动量矩阵与加权收益率...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    未检索到符合条件的板块轮动记录
                  </td>
                </tr>
              ) : (
                filteredItems.map((sec, idx) => {
                  const isHovered = hoveredSector === sec.sector_code;
                  const qStyle = getQuadrantStyle(sec.rrg_quadrant);
                  const isMomentumAbove100 = compareDecimalStrings(sec.rs_momentum, '100') >= 0;

                  return (
                    <tr
                      key={sec.sector_code}
                      onMouseEnter={() => setHoveredSector(sec.sector_code)}
                      onMouseLeave={() => setHoveredSector(null)}
                      style={{
                        borderBottom: '1px solid var(--border-subtle)',
                        background: isHovered ? 'var(--bg-surface-hover)' : idx % 2 === 1 ? 'var(--tag-inactive-bg)' : 'transparent',
                        transition: 'background 0.1s ease',
                      }}
                    >
                      {/* Sector Name & Code */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{sec.sector_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                          {sec.sector_code}
                        </div>
                      </td>

                      {/* Quadrant Badge */}
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            background: qStyle.bg,
                            color: qStyle.color,
                            border: `1px solid ${qStyle.border}`,
                          }}
                        >
                          {sec.rrg_quadrant}
                        </span>
                      </td>

                      {/* Pct Change */}
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontWeight: 700,
                          fontFamily: 'var(--font-mono)',
                          color: getColor(sec.pct_chg),
                        }}
                      >
                        {sec.pct_chg}%
                      </td>

                      {/* Turnover Total */}
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {sec.turnover_total_million}
                      </td>

                      {/* RS Diff */}
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          color: getColor(sec.rs_diff),
                        }}
                      >
                        {sec.rs_diff}%
                      </td>

                      {/* RS Ratio */}
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                        {sec.rs_ratio}
                      </td>

                      {/* RS Momentum */}
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'right',
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 600,
                          color: isMomentumAbove100 ? 'var(--color-bullish)' : 'var(--color-bearish)',
                        }}
                      >
                        {sec.rs_momentum}
                      </td>

                      {/* Breadth */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
                            <span style={{ color: upColor, fontWeight: 600 }}>{sec.breadth.advancing}</span> /{' '}
                            <span style={{ color: downColor }}>{sec.breadth.declining}</span> /{' '}
                            <span style={{ color: 'var(--text-muted)' }}>{sec.breadth.flat}</span>
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            ({sec.breadth.breadth_ratio}%)
                          </span>
                        </div>
                      </td>

                      {/* Leader Stock */}
                      <td style={{ padding: '12px 16px' }}>
                        {sec.leader_stock.symbol ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div>
                              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                {sec.leader_stock.name}
                              </span>
                              <span
                                style={{
                                  fontSize: '11px',
                                  color: 'var(--text-muted)',
                                  marginLeft: '4px',
                                  fontFamily: 'var(--font-mono)',
                                }}
                              >
                                {sec.leader_stock.symbol}
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 700,
                                fontFamily: 'var(--font-mono)',
                                color: getColor(sec.leader_stock.pct_chg),
                              }}
                            >
                              +{sec.leader_stock.pct_chg}%
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>--</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
